/**
 * The `/v1` surface's own vocabulary: how a path is built, how a page comes back, and
 * how a caller says which fields it wants.
 *
 * No transport here — see `../transport`. This is the grammar every v1 route shares.
 */

import type { Badges } from './moderation';

/** One query value. An array becomes a REPEATED parameter (`?status=a&status=b`),
 *  which is what doc 04 §1.6 requires of every multi-select — never a comma-joined
 *  scalar, which the server now rejects rather than silently mis-splits. */
export type QueryValue = string | number | boolean | null | undefined | (string | number)[];

export type QueryParams = Record<string, QueryValue>;

/** `?fields=` / `?include=` — doc 04 §1.7. Both are repeated parameters over the
 *  same parse, and an unknown value in either is a 400 (`unknown_field` /
 *  `unknown_include`) rather than a silent drop. Spread `selection(...)` into a
 *  route's params, or just pass `fields`/`include` directly — they are ordinary
 *  array values as far as `v1()` in `./http` is concerned. */
export interface Selection {
  fields?: string[];
  include?: string[];
}

export function selection(sel: Selection = {}): QueryParams {
  return {
    ...(sel.fields && sel.fields.length > 0 ? { fields: sel.fields } : {}),
    ...(sel.include && sel.include.length > 0 ? { include: sel.include } : {}),
  };
}

/** The community-scoped path prefix — doc 04 §1.1: `skool_id` lives in the PATH on
 *  every community-scoped route, and is never a query param or a body field.
 *
 *      community(19, '/members')                → /communities/19/members
 *      community(19, `/moderation/items/${k}/${id}`)
 */
export function community(skoolId: number, suffix = ''): string {
  return `/communities/${skoolId}${suffix}`;
}

/**
 * The ONE list envelope. Extend it to add the sibling blocks a route carries —
 * they sit beside `items`, never inside it, exactly as the backend subclasses
 * `ListEnvelope[T]`:
 *
 *     export interface InboxPage extends ListEnvelope<InboxItem> {
 *       community: CommunityRef;
 *       stats: InboxStats;
 *     }
 *
 * `next_cursor` is opaque — pass it back as `cursor`, never construct one.
 * `total` is null only where a route declares the count too expensive.
 * `limit`/`offset` are request state and are deliberately not echoed.
 */
export interface ListEnvelope<T> {
  items: T[];
  next_cursor: string | null;
  total: number | null;
}

/** A bulk mutation's response — doc 04 §1.2. `{"status": "ok"}` does not exist in
 *  v1: a write answers with what it changed, so nothing has to re-read to find out. */
export interface BulkResult<T> {
  affected: number;
  items: T[];
  badges?: Badges;
}

/** `{id, slug, name}` — carried once at the top level of every community-scoped
 *  response, never per row. */
export interface CommunityRef {
  id: number;
  slug: string;
  name: string;
}
